import { loc, type Lang, type Loc } from './cards.ts'

export type SpreadId =
  | 'one'
  | 'yesno'
  | 'three'
  | 'month'
  | 'destiny'
  | 'week'
  | 'five'
  | 'money'
  | 'feelings'
  | 'horseshoe'
  | 'paths'
  | 'celtic'
  | 'year'

/**
 * Вопрос по готовому раскладу.
 *
 * Дополнение к раскладу, а не новое гадание, поэтому дешевле самого дешёвого
 * платного расклада (30 ✦). Платят один раз, на входе, дальше границу держит
 * счётчик вопросов — то же устройство, что у джинна и нэнэ, и по той же
 * причине: между «вопросы ещё есть» и записью ответа лежит обращение к модели.
 *
 * Недоговорённый разговор возобновляется бесплатно: человек купил три вопроса,
 * а не один сеанс.
 */
export const FOLLOWUP_COST = 25
export const FOLLOWUP_TURNS = 3

/** Позиция карты на доске для раскладов сложной формы (координаты в px). */
export interface CardSlot {
  x: number
  y: number
  /** Поворот в градусах — вторая карта Кельтского креста лежит поперёк первой. */
  rot?: number
  z?: number
}

export interface SpreadLayout {
  kind: 'linear' | 'absolute'
  cardW: number
  cardH: number
  /** Размер доски для `absolute`; она центрируется по ширине экрана. */
  boardW?: number
  boardH?: number
  slots?: CardSlot[]
  /**
   * Подписывать ли карту названием прямо на доске. На мелких картах
   * (Кельтский крест, Колесо года) название не помещается — оно всё равно
   * есть в списке толкований под раскладом.
   */
  showTitles: boolean
}

/**
 * Раскладывает карты по кругу, начиная сверху и дальше по часовой стрелке.
 * Доска получается ровно по габаритам карт, без полей.
 */
function circleSlots(count: number, radius: number, cardW: number, cardH: number): CardSlot[] {
  const cx = radius + cardW / 2
  const cy = radius + cardH / 2
  return Array.from({ length: count }, (_, i) => {
    const angle = (-90 + (360 / count) * i) * (Math.PI / 180)
    return {
      x: Math.round(cx + radius * Math.cos(angle) - cardW / 2),
      y: Math.round(cy + radius * Math.sin(angle) - cardH / 2),
      z: i + 1,
    }
  })
}

/**
 * Раскладывает карты по дуге слева направо (подкова).
 *
 * Радиусы и размер карт подобраны так, чтобы соседние карты не задевали друг
 * друга габаритами: у краёв дуги они сближаются по вертикали, и именно там
 * раскладка ломается первой (проверяется тестом).
 *
 * **Радиуса два, и это не украшательство.** Дуга с одним радиусом выходит
 * широкой и низкой, а доска масштабируется ПО ШИРИНЕ — то есть такая дуга
 * упирается в края экрана, оставляя половину высоты пустой, и карты выходят
 * мелкими. Вытянув дугу вверх (`ry` больше `rx`), мы отдаём карте ту высоту,
 * которая всё равно простаивала.
 */
function arcSlots(
  count: number,
  rx: number,
  cardW: number,
  cardH: number,
  ry: number = rx,
): CardSlot[] {
  const cx = rx + cardW / 2
  const cy = ry + cardH / 2
  return Array.from({ length: count }, (_, i) => {
    const angle = (180 - (180 / (count - 1)) * i) * (Math.PI / 180)
    return {
      x: Math.round(cx + rx * Math.cos(angle) - cardW / 2),
      y: Math.round(cy - ry * Math.sin(angle) - cardH / 2),
      z: i + 1,
    }
  })
}

/*
  Подкова: узкая и высокая, а не широкая и низкая.

  Было 130 на оба радиуса — доска 300×194 при доступных 316×432, то есть по
  ширине впритык, а по высоте занята меньше половины. Карта выходила 42 точки,
  и владелец справедливо назвал их очень мелкими.

  Горизонтальный радиус ограничен снизу: у вершины дуги соседние карты
  расходятся по горизонтали на `rx/2`, и это должно быть больше ширины карты
  (40). При 85 расхождение 42.5 — с запасом в две точки, дальше ужимать нельзя.
  Вертикальный выбран так, чтобы доска не переросла отведённую высоту.
*/
const HORSESHOE_RX = 85
const HORSESHOE_RY = 200

const YEAR_RADIUS = 126
const YEAR_CARD_W = 40
const YEAR_CARD_H = 60

export interface SpreadDef {
  id: SpreadId
  n: number
  sym: string
  ru: string
  az: string
  en: string
  tr: string
  ruD: string
  azD: string
  enD: string
  trD: string
  /** Стоимость в Telegram Stars; 0 — расклад из бесплатного дневного лимита. */
  cost: number
  layout: SpreadLayout
  available: boolean
}

export const SPREADS: SpreadDef[] = [
  {
    id: 'one', n: 1, sym: '☾', ru: 'Карта дня', az: 'Günün kartı',
    en: 'Card of the day', tr: 'Günün kartı',
    ruD: 'Один совет на сегодня', azD: 'Bu gün üçün bir məsləhət',
    enD: 'One piece of advice for today', trD: 'Bugün için tek bir öğüt',
    cost: 0, available: true,
    layout: { kind: 'linear', cardW: 130, cardH: 206, showTitles: true },
  },
  {
    id: 'yesno', n: 1, sym: '⚖', ru: 'Да или нет', az: 'Bəli və ya xeyr',
    en: 'Yes or no', tr: 'Evet ya da hayır',
    ruD: 'Один ответ на ваш вопрос', azD: 'Sualınıza bir cavab',
    enD: 'One answer to your question', trD: 'Sorunuza tek bir cevap',
    cost: 0, available: true,
    layout: { kind: 'linear', cardW: 130, cardH: 206, showTitles: true },
  },
  {
    id: 'three', n: 3, sym: '✦', ru: 'Три карты', az: 'Üç kart',
    en: 'Three cards', tr: 'Üç kart',
    ruD: 'Прошлое · Настоящее · Будущее', azD: 'Keçmiş · İndi · Gələcək',
    enD: 'Past · Present · Future', trD: 'Geçmiş · Şimdi · Gelecek',
    cost: 0, available: true,
    layout: { kind: 'linear', cardW: 92, cardH: 146, showTitles: true },
  },
  {
    id: 'month', n: 4, sym: '☾', ru: 'Месяц', az: 'Ay',
    en: 'The month', tr: 'Ay',
    ruD: 'Четыре недели впереди', azD: 'Qarşıdakı dörd həftə',
    enD: 'The four weeks ahead', trD: 'Önümüzdeki dört hafta',
    cost: 40, available: true,
    layout: { kind: 'linear', cardW: 74, cardH: 118, showTitles: false },
  },
  {
    id: 'destiny', n: 4, sym: '✧', ru: 'Путь и предназначение', az: 'Yol və məqsəd',
    en: 'Path and purpose', tr: 'Yol ve amaç',
    ruD: 'Дар, призвание и следующий шаг', azD: 'Bacarıq, çağırış və növbəti addım',
    enD: 'Your gift, your calling and the next step', trD: 'Yeteneğiniz, çağrınız ve bir sonraki adım',
    cost: 40, available: true,
    layout: {
      kind: 'absolute',
      showTitles: false,
      cardW: 62, cardH: 98,
      boardW: 210, boardH: 318,
      // Ромб: дар сверху, призвание и помеха по бокам, шаг внизу.
      slots: [
        { x: 74, y: 0 },
        { x: 0, y: 110 },
        { x: 148, y: 110 },
        { x: 74, y: 220 },
      ],
    },
  },
  {
    id: 'week', n: 7, sym: '❂', ru: 'Неделя', az: 'Həftə',
    en: 'The week', tr: 'Hafta',
    ruD: 'По карте на каждый день', azD: 'Hər gün üçün bir kart',
    enD: 'A card for every day', trD: 'Her gün için bir kart',
    cost: 30, available: true,
    layout: {
      kind: 'absolute',
      showTitles: false,
      cardW: 56, cardH: 88,
      boardW: 254, boardH: 186,
      // Четыре дня в первом ряду, три во втором — иначе неделя не влезает
      // в ширину экрана.
      slots: [
        { x: 0, y: 0 }, { x: 66, y: 0 }, { x: 132, y: 0 }, { x: 198, y: 0 },
        { x: 33, y: 98 }, { x: 99, y: 98 }, { x: 165, y: 98 },
      ],
    },
  },
  {
    id: 'five', n: 5, sym: '♡', ru: 'Расклад на любовь', az: 'Sevgi açılışı',
    en: 'Love spread', tr: 'Aşk açılımı',
    ruD: 'Пять карт о ваших отношениях', azD: 'Münasibətlər haqqında beş kart',
    enD: 'Five cards about your relationship', trD: 'İlişkiniz hakkında beş kart',
    cost: 50, available: true,
    layout: {
      kind: 'absolute',
      // Имена младших арканов («Королева Пентаклей») в карту 52px не влезают
      // и ломаются на три строки — они и так есть в списке под раскладом.
      showTitles: false,
      cardW: 52, cardH: 82,
      boardW: 218, boardH: 274,
      // Крест: вы — партнёр — связь по горизонтали, совет сверху, итог снизу.
      // Шаг по вертикали — высота карты плюс зазор: при прежних 70 верхняя и
      // нижняя карты заходили на среднюю на 12 пикселей.
      slots: [
        { x: 83,  y: 96 },
        { x: 0,   y: 96 },
        { x: 166, y: 96 },
        { x: 83,  y: 4 },
        { x: 83,  y: 188 },
      ],
    },
  },
  {
    id: 'money', n: 5, sym: '◈', ru: 'Работа и деньги', az: 'İş və pul',
    en: 'Work and money', tr: 'İş ve para',
    ruD: 'Пять карт о деле и достатке', azD: 'İş və bolluq haqqında beş kart',
    enD: 'Five cards about your work and means', trD: 'İşiniz ve kazancınız hakkında beş kart',
    cost: 50, available: true,
    layout: {
      kind: 'absolute',
      showTitles: false,
      cardW: 62, cardH: 98,
      boardW: 210, boardH: 208,
      // Три карты о положении дел, под ними шаг и итог.
      slots: [
        { x: 0, y: 0 }, { x: 74, y: 0 }, { x: 148, y: 0 },
        { x: 37, y: 110 }, { x: 111, y: 110 },
      ],
    },
  },
  {
    id: 'feelings', n: 5, sym: '♥', ru: 'Чувства другого', az: 'Qarşı tərəfin hissləri',
    en: 'Their feelings', tr: 'Karşı tarafın hisleri',
    ruD: 'Пять карт о чувствах другого', azD: 'Qarşı tərəfin hissləri haqqında beş kart',
    enD: 'Five cards about what the other feels', trD: 'Karşı tarafın hisleri hakkında beş kart',
    cost: 70, available: true,
    layout: {
      kind: 'absolute',
      showTitles: false,
      cardW: 62, cardH: 98,
      boardW: 210, boardH: 318,
      // Чувства, мысли и намерения в ряд; ниже — помеха и к чему всё идёт.
      slots: [
        { x: 0, y: 0 }, { x: 74, y: 0 }, { x: 148, y: 0 },
        { x: 74, y: 110 },
        { x: 74, y: 220 },
      ],
    },
  },
  {
    id: 'horseshoe', n: 7, sym: '⌒', ru: 'Подкова', az: 'Nal',
    en: 'The horseshoe', tr: 'Nal',
    ruD: 'Семь карт о ходе дела', azD: 'İşin gedişatı haqqında yeddi kart',
    enD: 'Seven cards on how the matter unfolds', trD: 'İşin gidişatı üzerine yedi kart',
    cost: 80, available: true,
    layout: {
      kind: 'absolute',
      showTitles: false,
      cardW: 40, cardH: 64,
      boardW: HORSESHOE_RX * 2 + 40,
      boardH: HORSESHOE_RY + 64,
      slots: arcSlots(7, HORSESHOE_RX, 40, 64, HORSESHOE_RY),
    },
  },
  {
    id: 'paths', n: 6, sym: '⑂', ru: 'Два пути', az: 'İki yol',
    en: 'Two paths', tr: 'İki yol',
    ruD: 'Сравнение двух решений', azD: 'İki qərarın müqayisəsi',
    enD: 'A comparison of two decisions', trD: 'İki kararın karşılaştırması',
    cost: 90, available: true,
    layout: {
      kind: 'absolute',
      showTitles: false,
      cardW: 62, cardH: 98,
      boardW: 180, boardH: 318,
      // Две колонки: слева первый путь, справа второй.
      slots: [
        { x: 0, y: 0 }, { x: 0, y: 110 }, { x: 0, y: 220 },
        { x: 118, y: 0 }, { x: 118, y: 110 }, { x: 118, y: 220 },
      ],
    },
  },
  {
    id: 'celtic', n: 10, sym: '✧', ru: 'Кельтский крест', az: 'Kelt xaçı',
    en: 'Celtic cross', tr: 'Kelt haçı',
    ruD: 'Глубокий разбор ситуации', azD: 'Vəziyyətin dərin təhlili',
    enD: 'A deep look at the situation', trD: 'Durumun derin bir çözümlemesi',
    cost: 120, available: true,
    layout: {
      kind: 'absolute',
      showTitles: false,
      cardW: 46, cardH: 72,
      boardW: 282, boardH: 330,
      // Крест из шести карт слева и «посох» из четырёх справа.
      slots: [
        { x: 104, y: 120, z: 3 },
        { x: 104, y: 120, rot: 90, z: 4 },
        { x: 104, y: 210, z: 2 },
        { x: 104, y: 30,  z: 1 },
        { x: 36,  y: 120, z: 2 },
        { x: 172, y: 120, z: 2 },
        { x: 236, y: 246, z: 1 },
        { x: 236, y: 168, z: 1 },
        { x: 236, y: 90,  z: 1 },
        { x: 236, y: 12,  z: 1 },
      ],
    },
  },
  {
    id: 'year', n: 12, sym: '❂', ru: 'Колесо года', az: 'İl çarxı',
    en: 'Wheel of the year', tr: 'Yıl çarkı',
    ruD: 'Прогноз на 12 месяцев', azD: '12 ay üçün proqnoz',
    enD: 'A forecast for 12 months', trD: '12 ay için bir öngörü',
    cost: 200, available: true,
    layout: {
      kind: 'absolute',
      showTitles: false,
      cardW: YEAR_CARD_W, cardH: YEAR_CARD_H,
      // Круг ровно по габаритам карт: 2·радиус + размер карты.
      boardW: YEAR_RADIUS * 2 + YEAR_CARD_W,
      boardH: YEAR_RADIUS * 2 + YEAR_CARD_H,
      slots: circleSlots(12, YEAR_RADIUS, YEAR_CARD_W, YEAR_CARD_H),
    },
  },
]

export function spreadById(id: SpreadId): SpreadDef {
  return SPREADS.find((s) => s.id === id) ?? SPREADS[1]
}

/**
 * Расклады, которые можно повторять в тот же день.
 *
 * Их ответ зависит от вопроса, который человек задаёт заново: в «Двух путях»
 * он вводит его текстом, в «Да или нет» держит в голове. Остальные расклады
 * отвечают на положение дел, а оно за вечер не меняется — второй такой же
 * расклад дал бы противоречивый ответ на тот же вопрос и списал бы ещё раз.
 */
const REPEATABLE: SpreadId[] = ['yesno', 'paths']

/**
 * Скидка за повтор платного расклада в тот же день.
 *
 * Второй расклад того же вида за день человек делает под ДРУГОЙ темой — то
 * есть с другим вопросом и новыми картами. Платить полную цену второй раз он
 * не должен: первый уже оплачен, и это доплата за другой угол, а не новая
 * покупка. Тридцать процентов — решение владельца от 2026-09-04.
 *
 * Округляем до пяти: цены у нас круглые (30, 50, 80, 120), и «84 ✦» выглядело
 * бы как ошибка, а не как скидка.
 *
 * Правило живёт здесь, а не у сервера, потому что нужно обоим: сервер считает
 * СПИСАНИЕ, клиент показывает цену на кнопке. Разойдутся — человек увидит одно,
 * а спишется другое. Решает при этом всё равно сервер: только он знает, был ли
 * сегодня первый расклад.
 */
export const REPEAT_DISCOUNT = 0.3

export function repeatCost(cost: number): number {
  if (cost <= 0) return 0
  return Math.max(5, Math.round((cost * (1 - REPEAT_DISCOUNT)) / 5) * 5)
}

/** Можно ли сделать этот расклад ещё раз сегодня. */
export function repeatableToday(id: SpreadId): boolean {
  return REPEATABLE.includes(id)
}

export function spreadName(s: SpreadDef, lang: Lang): string {
  return loc(s, lang)
}

export function spreadDesc(s: SpreadDef, lang: Lang): string {
  return loc({ ru: s.ruD, az: s.azD, en: s.enD, tr: s.trD }, lang)
}

/** Месяцы в именительном падеже — подписи позиций Колеса года. */
const DAYS: Record<Lang, string[]> = {
  ru: ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'],
  az: ['Bazar ertəsi', 'Çərşənbə axşamı', 'Çərşənbə', 'Cümə axşamı', 'Cümə', 'Şənbə', 'Bazar'],
  en: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  tr: ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'],
}

const WEEKS: Record<Lang, string[]> = {
  ru: ['Первая неделя', 'Вторая неделя', 'Третья неделя', 'Четвёртая неделя'],
  az: ['Birinci həftə', 'İkinci həftə', 'Üçüncü həftə', 'Dördüncü həftə'],
  en: ['First week', 'Second week', 'Third week', 'Fourth week'],
  tr: ['Birinci hafta', 'İkinci hafta', 'Üçüncü hafta', 'Dördüncü hafta'],
}

const MONTHS: Record<Lang, string[]> = {
  ru: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
       'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  az: ['Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'İyun',
       'İyul', 'Avqust', 'Sentyabr', 'Oktyabr', 'Noyabr', 'Dekabr'],
  en: ['January', 'February', 'March', 'April', 'May', 'June',
       'July', 'August', 'September', 'October', 'November', 'December'],
  tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
       'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
}

/**
 * Подписи позиций расклада.
 *
 * `startMonth` — точка отсчёта для раскладов-прогнозов, и смысл у неё разный:
 * Колесу года это месяц (0–11), Неделе — день недели (0 — понедельник).
 * Оба расклада смотрят вперёд, поэтому начинаются с текущего дня или месяца,
 * а не с января и не с понедельника. Значение выбирает клиент в момент
 * расклада и передаёт серверу, чтобы подписи в интерфейсе и в промпте
 * совпадали даже на стыке суток.
 */
export function positionLabels(id: SpreadId, lang: Lang, startMonth = 0): string[] {
  /** Одна подпись на четырёх языках. */
  const byLang = (v: Record<Lang, string>) => loc(v, lang)
  /** Список подписей: порядок языков тот же, что у `LANGS`. */
  const pick = (ru: string[], az: string[], en: string[], tr: string[]) =>
    ({ ru, az, en, tr })[lang] ?? en
  switch (id) {
    case 'year': {
      const names = MONTHS[lang]
      const start = ((Math.trunc(startMonth) % 12) + 12) % 12
      return Array.from({ length: 12 }, (_, i) => names[(start + i) % 12])
    }
    case 'one':
      return [byLang({ ru: 'Совет дня', az: 'Günün məsləhəti', en: 'Advice of the day', tr: 'Günün öğüdü' })]
    case 'yesno':
      return [byLang({ ru: 'Ответ', az: 'Cavab', en: 'The answer', tr: 'Cevap' })]
    case 'month':
      // Недели считаем от текущей: расклад — прогноз вперёд, а не календарь.
      return WEEKS[lang]
    case 'destiny':
      return pick(['Дар', 'Призвание', 'Что сбивает', 'Следующий шаг'],
                  ['Bacarıq', 'Çağırış', 'Nə yayındırır', 'Növbəti addım'],
                  ['Gift', 'Calling', 'What throws you off', 'Next step'], ['Yetenek', 'Çağrı', 'Sizi saptıran', 'Sonraki adım'])
    case 'week': {
      // Круг начинается с сегодняшнего дня: это прогноз на неделю вперёд,
      // а не таблица с понедельника.
      const days = DAYS[lang]
      const start = ((Math.trunc(startMonth) % 7) + 7) % 7
      return Array.from({ length: 7 }, (_, i) => days[(start + i) % 7])
    }
    case 'money':
      return pick(['Сейчас', 'Что мешает', 'Что помогает', 'Шаг', 'Итог'],
                  ['İndi', 'Nə mane olur', 'Nə kömək edir', 'Addım', 'Nəticə'],
                  ['Now', 'What hinders', 'What helps', 'Step', 'Outcome'], ['Şimdi', 'Ne engelliyor', 'Ne yardım ediyor', 'Adım', 'Sonuç'])
    case 'feelings':
      return pick(['Чувства', 'Мысли', 'Намерения', 'Что мешает', 'К чему идёт'],
                  ['Hisslər', 'Düşüncələr', 'Niyyətlər', 'Nə mane olur', 'Nəyə doğru gedir'],
                  ['Feelings', 'Thoughts', 'Intentions', 'What hinders', 'Where it leads'], ['Duygular', 'Düşünceler', 'Niyetler', 'Ne engelliyor', 'Nereye gidiyor'])
    case 'horseshoe':
      return pick(['Прошлое', 'Настоящее', 'Скрытое', 'Помеха', 'Окружение', 'Совет', 'Итог'],
                  ['Keçmiş', 'İndi', 'Gizli olan', 'Maneə', 'Ətraf', 'Məsləhət', 'Nəticə'],
                  ['Past', 'Present', 'What is hidden', 'The obstacle', 'Those around', 'Advice', 'Outcome'], ['Geçmiş', 'Şimdi', 'Gizli olan', 'Engel', 'Çevre', 'Öğüt', 'Sonuç'])
    case 'paths':
      return pick(['Первый путь: сейчас', 'Первый путь: шаг', 'Первый путь: итог',
                   'Второй путь: сейчас', 'Второй путь: шаг', 'Второй путь: итог'],
                  ['Birinci yol: indi', 'Birinci yol: addım', 'Birinci yol: nəticə',
                   'İkinci yol: indi', 'İkinci yol: addım', 'İkinci yol: nəticə'],
                  ['First path: now', 'First path: step', 'First path: outcome', 'Second path: now', 'Second path: step', 'Second path: outcome'], ['Birinci yol: şimdi', 'Birinci yol: adım', 'Birinci yol: sonuç', 'İkinci yol: şimdi', 'İkinci yol: adım', 'İkinci yol: sonuç'])
    case 'three':
      return pick(['Прошлое', 'Настоящее', 'Будущее'], ['Keçmiş', 'İndi', 'Gələcək'],
                  ['Past', 'Present', 'Future'], ['Geçmiş', 'Şimdi', 'Gelecek'])
    case 'five':
      return pick(['Вы', 'Партнёр', 'Связь', 'Совет', 'Итог'],
                  ['Siz', 'Partnyor', 'Bağ', 'Məsləhət', 'Nəticə'],
                  ['You', 'Partner', 'The bond', 'Advice', 'Outcome'], ['Siz', 'Partner', 'Bağ', 'Öğüt', 'Sonuç'])
    case 'celtic':
      return pick(['Суть', 'Помеха', 'Прошлое', 'Будущее', 'Цель', 'Основа', 'Вы', 'Среда', 'Надежды', 'Итог'],
                  ['Mahiyyət', 'Maneə', 'Keçmiş', 'Gələcək', 'Məqsəd', 'Təməl', 'Siz', 'Mühit', 'Ümidlər', 'Nəticə'],
                  ['Heart', 'Obstacle', 'Past', 'Future', 'Goal', 'Foundation', 'You', 'Around you', 'Hopes', 'Outcome'], ['Öz', 'Engel', 'Geçmiş', 'Gelecek', 'Amaç', 'Temel', 'Siz', 'Çevre', 'Umutlar', 'Sonuç'])
    default:
      return []
  }
}

/**
 * Ответ расклада «Да или нет».
 *
 * Считаем мы, а не модель: ответ должен совпадать с тем, что человек видит на
 * карте, и не меняться от запуска к запуску. Правило традиционное и самое
 * простое из принятых — прямая карта отвечает «да», перевёрнутая «нет».
 * Модели этот ответ передаётся готовым, её дело — объяснить его картой.
 */
export function yesNoAnswer(card: { reversed: boolean }): 'yes' | 'no' {
  return card.reversed ? 'no' : 'yes'
}

/**
 * Подпись ответа для интерфейса и промпта.
 *
 * Словарями, а не через `if (lang === 'ru')`: прежняя развилка отдавала
 * азербайджанское «Bəli/Xeyr» и англичанину, и турку — и на плашке в раскладе,
 * и в промпте модели. Тест этого не поймал, потому что проверял только `ru`
 * и `az`; теперь проверяются все четыре.
 */
const YES: Loc = { ru: 'Да', az: 'Bəli', en: 'Yes', tr: 'Evet' }
const NO: Loc = { ru: 'Нет', az: 'Xeyr', en: 'No', tr: 'Hayır' }

export function yesNoLabel(answer: 'yes' | 'no', lang: Lang): string {
  return loc(answer === 'yes' ? YES : NO, lang)
}
